import type { LandingItemsLocation } from "$lib/serv/locations";
import type { Address, GeoPoint } from "proto/address";
import {falsy, strcmp} from "./func";

/**
 * Checks if address has a lat/lon
 * Can be as keys or in geo_location object
 * @returns if address has latitude and longitude
 */
export function hasGeo(addr: Partial<Address>): boolean {
  if (!addr) return false;
	if (addr.latitude && addr.longitude) return true;
	if (addr.geo_location) return !!addr.geo_location.lat && !!addr.geo_location.lon;
	return false;
}

/**
 * Forces an address to match the proto definition
 * Removes excess fields
 * @returns a proto address used by both food and groc
 */
export function cleanAddress(address: LandingItemsLocation): Address | null {

	if (!hasGeo(address)) return null; // if no lat/lon address is invalid

	const a = {} as Address;

	// geo
	if (address.geo_location) {
		a.geo_location = {} as GeoPoint;
		if (address.geo_location.lat) a.geo_location.lat = +address.geo_location.lat;
		if (address.geo_location.lon) a.geo_location.lon = +address.geo_location.lon;
	// latitude/longitude
	} else if (address.latitude || address.longitude) {
		a.geo_location = {} as GeoPoint;
		if (address.latitude) a.geo_location.lat = +address.latitude;
		if (address.longitude) a.geo_location.lon = +address.longitude;
	}
	if (address.street_number) a.street_number = address.street_number;
	if (address.street_name) a.street_name = address.street_name;
	a.suburb = address.suburb ?? ''; // breaks GET quote if undefined
	// Breaks food cart
	// if (address.town) a.town = address.town;
	if (address.province) a.province = address.province;
	if (address.postal_code) a.postal_code = address.postal_code;
	if (address.building_type) a.building_type = address.building_type;
	if (address.complex) a.complex = address.complex;
	if (address.label) a.label = address.label;

	return a;
}

export const ADDRESS_PROPS = [
	// 'geo_location.lat',
	// 'geo_location.lon',
	'street_name',
	'street_number',
	'label',
	'suburb',
	'town',
	'building_type',
	'complex',
	'province',
	'postal_code',
];

/**
  * Utility class for manipulating addresses
  **/
export class xAddress {
  public latitude = "";
  public longitude = "";
  public street_number = "";
  public street_name = "";
  public suburb = "";
  public town = "";
  public province = "";
  public postal_code = "";
  // meta
  public building_type = "";
  public complex =  "";
  public label = "";
  public saved_delivery_instruction = "";
  [k: string]: any;

  /**
    * Creates an instance from the supplied address object, copying the common property values.
    * @param address - A plain partial Address.
    */
  constructor(address: Partial<Address> = {}) {
    Object.keys(address).forEach(k => {
      if (k in this)
        this[k] = (<any>address)[k];
    })
  }

  /**
    * Creates a clone of the current instance, setting all properties to empty string, except those specified in the keys param.
    * It returns a reference to the clone to allow method chaining.
    * @param keys - An array of address properties to keep.
    * @returns reference to clone.
    */
  keep(...keys: string[]): xAddress {
    const addr = new xAddress();
    keys.forEach(k => {
      if (k in this)
        addr[k] = this[k];
    })
    return addr;
  }

  /**
    * Creates a clone of the current instance, setting all properties specified in the keys param to empty string, all others remain as is.
    * It returns a reference to the clone to allow method chaining.
    * @param keys - An array of address properties to remove.
    * @returns reference to clone.
    */
  remove(...keys: string[]): xAddress {
    const addr = new xAddress(this);
    keys.forEach(k => {
      if (k in this)
        addr[k] = "";
    })
    return addr;
  }

  /**
    * Checks if other address is equal to this.
    * Equality is dependent on the level param.
    *
    * level = "partial", only compares coordinates, street_number and street_name.
    * level = "complete", includes all the partial equality props including
    * building_type, complex, saved_delivery_instruction and label.
    *
    * NB: Address equality is unstable, due to the fact that there are incomplete / malformed
    * addresses stored in the prod DB. Rules regarding which fields are required and absolutely necessary
    * have not been documented.
    *
    * @param other - Other address.
    * @param level - level of equality, partial or complete.
    * @returns boolean.
    */
  equals(other: Partial<Address>, level: "partial" | "complete"): boolean {
    const lat = other.latitude || String(other.geo_location?.lat ?? "");
    const lon = other.longitude || String(other.geo_location?.lon ?? "");
    const partial =
      strcmp(lat ?? "", this.latitude) &&
      strcmp(lon ?? "", this.longitude) &&
      strcmp(other.street_number ?? "", this.street_number) &&
      strcmp(other.street_name ?? "", this.street_name);

    if (level === "partial") {
      return partial;
    }

    const complete =
      strcmp(other.building_type ?? "", this.building_type) &&
      strcmp(other.complex ?? "", this.complex) &&
      strcmp(other.saved_delivery_instruction ?? "", this.saved_delivery_instruction) &&
      strcmp(other.label ?? "", this.label);

    return partial && complete;
  }

  /**
    * Removes falsy values.
    * @returns reference to clean clone.
    */
  clean(): Partial<Address> {
    const addr: Record<string, any> = this.toPlain();
    const keys = Object.keys(addr);
    for (const k of keys) {
      if (falsy(addr[k])) {
        delete addr[k];
      }
    }
    return addr;
  }

  /**
    * Creates a plain clone of the current instance.
    * @returns reference to plain clone.
    */
  toPlain(): Address {
    const addr = {};
    Object.assign(addr, this);
    return <Address>addr;
  }

  /**
    * Extracts the props that can be manually changed via AddressFrom.
    * @returns reference to clone including only manually changeable props.
    */
  toChangeable(): Partial<Address> {
    return this.keep("street_number", "building_type", "complex", "saved_delivery_instruction", "label").clean();
  }

  /**
    * Converts this into a string.
    * @param separator - Separator for main address properties.
    * @returns address as string.
    */
  toStr(separator = ', '): string {
    const { street_number, street_name, suburb, town, province, postal_code } = this;
  
    const str = [];
    street_number ? str.push(street_number) : 0;
    street_name ? str.push(street_name) : 0;
    const street = str.join(" ");
  
    const addr = [];
    street ? addr.push(street) : 0;
    suburb ? addr.push(suburb) : 0;
    town ? addr.push(town) : 0;
    province ? addr.push(province) : 0;
    postal_code ? addr.push(postal_code) : 0;
  
    return addr.join(separator);
  }
}

/**
  * Alias function for xAddress constructor.
  * @param address - A plain partial Address.
  * @returns xAddress instance.
  */
export const xaddress = (address?: Partial<Address>): xAddress => new xAddress(address);

export class LV {
  constructor(readonly label: string, readonly value: string) {}
}

// export type BuildingType = "complex / estate" | "apartment" | "house" | "office" | "hotel / bed and breakfast";
// export type BuildingLabel = "Complex/Estate" | "Apartment" | "House" | "Office" | "Hotel/B&B";

export const buildingTypes = [
  new LV("Complex/Estate", "complex / estate"),
  new LV("Apartment" ,"apartment"),
  new LV("House" ,"house"),
  new LV("Office" ,"office"),
  new LV("Hotel/B&B" ,"hotel / bed and breakfast")
];

/**
  * Given a building_type, it checks if that building requires a supporting complex.
  * @param building_type - Address building_type.
  * @returns boolean.
  */
export function requiresComplex(building_type: string): boolean {
  return ["complex / estate", "apartment", "office", "hotel / bed and breakfast"].includes(building_type);
}

/**
  * Given a specific building_type, it returns the matching placeholder for the required complex.
  * @param building_type - Address building_type.
  * @returns Complex placeholder for the specific building_type.
  */
export function getComplexPlaceholder(building_type: string): string {
  switch (building_type) {
    case "complex / estate":
      return "Complex/estate name & house details";

    case "apartment":
      return "Unit no & complex name";

    case "office":
      return "Company, building name & floor no";

    case "hotel / bed and breakfast":
      return "Room no & hotel name";

    default:
      return "";
  }
}

export const addressLabels = [
  new LV("My Couch", "home"),
  new LV("My Desk", "work"),
  new LV("My 2nd Home", "other"),
  new LV("Don't save", ""),
]

/**
  * Given a specific address label, it returns the matching display label.
  * @param label - Address label.
  * @returns Display label.
  */
export function getAddressLabel(label: string): string {
  switch (label) {
    case "home":
      return "My Couch";

    case "work":
      return "My Desk";

    case "other":
      return "My 2nd Home";

    default:
      return "";
  }
}

export function getAddressIcon(addr: Address) {
  if (addr.label === 'work') return 'icon-briefcase-28';
  if (addr.label === 'homw') return 'icon-couch-28';
  return 'icon-home-28';
}

export type Stage = "blank" | "geo" | "main" | "total";

/**
  * Given an address, it determines how complete the address is.
  * @param a - Address.
  * @returns Completion stage.
  */
export function getCompletion(address: Address): Stage {
  const geo = address.latitude && address.longitude;

  const main =
    geo && address.street_number && address.street_name && address.suburb && address.town && address.province && address.postal_code;

  const total =
    main &&
    address.building_type &&
    address.label &&
    (requiresComplex(address.building_type) ? address.complex : true);

  if (total) return "total";
  if (main) return "main";
  if (geo) return "geo";
  return "blank";
}
